// TipArticlePage.tsx
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Link } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet-async';
import { TIPS_CATEGORIES, TIPS_ENTRIES, TipCategory, getTipsByCategory } from './type';
import { useNavigate } from 'react-router-dom';


interface Props {
    category: TipCategory;
}

const TipArticlePage: FC<Props> = ({ category }) => {
    const { slug } = useParams();
    const [markdownContent, setMarkdownContent] = useState<string | null>(null);
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);

    const entry = TIPS_ENTRIES.find((e) => e.category === category && e.slug === slug);

    const TipsBreadcrumb: FC<{ category: TipCategory }> = ({ category }) => {
        const navigate = useNavigate();
        const { label } = TIPS_CATEGORIES[category];

        return (
            <Box display="flex" alignItems="center" gap={0.5} mb={2} flexWrap="wrap">
                <Box
                    onClick={() => navigate('/tips')}
                    sx={{
                        color: 'text.secondary',
                        fontSize: 14,
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    Conseils
                </Box>
                <Typography color="text.disabled" fontSize={14}>/</Typography>
                <Typography color="text.secondary" fontSize={14}>{label}</Typography>
            </Box>
        );
    };

    const TipsRelated: FC<{ category: TipCategory; currentSlug: string }> = ({ category, currentSlug }) => {
        const navigate = useNavigate();
        const autresArticles = getTipsByCategory(category).filter((e) => e.slug !== currentSlug);

        if (autresArticles.length === 0) return null;

        return (
            <Box mt={6} pt={3} borderTop="1px solid" borderColor="divider" width="100%">
                <Box
                    onClick={() => navigate('/tips')}
                    sx={{
                        color: 'text.secondary',
                        fontSize: 14,
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    ← Retour à tous les conseils
                </Box>
            </Box>
        );
    };

    useEffect(() => {
        if (!entry) {
            setNotFound(true);
            setLoading(false);
            return;
        }

        setLoading(true);
        setNotFound(false);

        fetch(`/tips/${category}/${slug}.md`)
            .then((res) => {
                if (!res.ok) throw new Error('not found');
                return res.text();
            })
            .then((rawText) => {
                setMarkdownContent(rawText);
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [category, slug, entry]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (notFound || !entry || !markdownContent) {
        return (
            <Box p={4}>
                <Typography variant="h5">Article introuvable</Typography>
            </Box>
        );
    }

    return (
        <Box component="article" maxWidth={800} mx="auto" p={2} display="flex" flexDirection="column" alignItems="flex-start">
            <Helmet>
                <title>{`${entry.title} | ExpertTurf`}</title>
                <meta name="description" content={entry.description} />
            </Helmet>
            <TipsBreadcrumb category={category} />

            <Typography variant="h3" component="h1" color='secondary.dark' gutterBottom fontWeight={700} textAlign="left" width="100%">
                {entry.title}
            </Typography>

            <Typography variant="subtitle1" color="text.secondary" mb={3} textAlign="left" width="100%">
                {entry.description}
            </Typography>

            <ReactMarkdown
                components={{
                    h1: ({ children }) => (
                        <Typography variant="h3" component="h1" fontWeight={700} color="primary.main" mt={4} mb={2} textAlign="left" width="100%">
                            {children}
                        </Typography>
                    ),
                    h2: ({ children }) => (
                        <Typography
                            variant="h5"
                            component="h2"
                            fontWeight={700}
                            mt={4}
                            mb={2}
                            textAlign="left"
                            width="100%"
                            sx={{
                                position: 'relative',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1.2,
                                '&::before': {
                                    content: '""',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: 'secondary.main',
                                    flexShrink: 0,
                                },
                            }}
                        >
                            {children}
                        </Typography>
                    ),
                    h3: ({ children }) => (
                        <Typography variant="h6" component="h3" fontWeight={600} mt={3} mb={1} color="text.primary" textAlign="left" width="100%">
                            {children}
                        </Typography>
                    ),
                    p: ({ children }) => (
                        <Typography variant="body1" color="text.primary" mb={2} lineHeight={1.7} textAlign="left" width="100%">
                            {children}
                        </Typography>
                    ),
                    // a: ({ href, children }) => (
                    //     <MuiLink component={Link} to={href || '#'} color="secondary.main" fontWeight={600}>
                    //         {children}
                    //     </MuiLink>
                    // ),
                    strong: ({ children }) => (
                        <Typography component="span" fontWeight={700} color="primary.main">
                            {children}
                        </Typography>
                    ),
                }}
            >
                {markdownContent}
            </ReactMarkdown>
            <TipsRelated category={category} currentSlug={slug!} />
        </Box>
    );
};

export default TipArticlePage;